// @flow

import React, { Component } from 'react';
import FocusBody from 'components/FocusBody';
import Page from 'components/Page';
import logoBlackWide from 'themes/images/logo-black-wide.png';
import FormRow from 'components/FormRow';
import * as Yup from 'yup';
import { injectIntl } from 'react-intl';
import View from 'components/View';
import { DetailBottomBarConfirm } from 'components/DetailBottomBarItem';
import DetailBottomBar, { NotificationBar } from 'components/DetailBottomBar';
import Heading from 'components/Heading';
import type { Style, Theme } from 'types';
import Text from 'components/Text';
import Version from 'components/Version';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import ValidationMessage from '../../components/ValidationMessage';
import ImageBottomBar from '../../components/ImageBottomBar';
import FocusBottomBar from '../../components/FocusBottomBar';
import FocusContent from '../../components/FocusContent';
import TextInput from '../../components/TextInput';

const specCodeMutation = gql`
  mutation registrationCode($code: String) {
    registrationCode(data: { code: $code }) {
      registrationCode {
        record
        code
        cards
        creator {
          record
          username
          email
        }
      }
    }
  }
`;

const styles = {
  page: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  text: (props, { components: { FocusPage } }) => ({
    ...FocusPage.text
  }),
  img: {
    marginLeft: 20,
    height: 20
  },
  error: (props, { components: { Login } }) => ({
    ...Login.error
  }),
  header: (props, theme: Theme): Style => ({
    ...theme.components.ExternalModule.SpecificCodeHeader,
    fontSize: '18px',
    width: '420px',
    alignItems: 'flex-start'
  }),
  regCodeText: {
    padding: '15px',
    width: '420px',
    backgroundColor: '#F1F1F1'
  },
  withValidationMessage: {
    left: 'calc(100% + 15px)',
    top: '32px'
  }
};

export class SpecificCodeForm extends Component {
  state = {
    codeInputError: false
  };

  validationSchema = intl =>
    Yup.object().shape({
      code: Yup.string().required(intl.formatMessage({ id: 'general.required-field' }))
    });

  continue = e => {
    const { nextStep } = this.props;
    e.preventDefault();
    this.validateForm(() => {
      nextStep();
    });
  };

  validateForm = afterSuccess => {
    const { values, intl } = this.props;
    this.validationSchema(intl)
      .isValid({
        code: values.code
      })
      .then(valid => {
        if (valid) {
          this.setState({ codeInputError: false });
          afterSuccess();
        } else {
          this.setState({ codeInputError: true });
        }
      });
  };

  handleChange = fieldName => e => {
    const { handleValueChange, intl } = this.props;
    const fieldValue = e.target.value;

    this.validationSchema(intl)
      .isValid({
        [fieldName]: fieldValue
      })
      .then(valid => {
        handleValueChange(fieldName, fieldValue);
        this.setState({ codeInputError: !valid });
      });
  };

  render() {
    const { values, intl } = this.props;
    const { codeInputError } = this.state;

    const ValidationMessageRef = codeInputError ? (
      <ValidationMessage
        message={intl.formatMessage({ id: 'general.required-field' })}
        tipPosition="left"
        style={styles.withValidationMessage}
      />
    ) : (
      <span />
    );

    const DynamicBottomBar = ({ checkCode, loading, error }) => (
      <View style={{ flexDirection: 'column' }}>
        {error && (
          <NotificationBar text={intl.formatMessage({ id: 'specific-code.invalid-code' })} />
        )}

        <FocusBottomBar>
          <ImageBottomBar>
            <img src={logoBlackWide} alt="LoyalMAN" style={styles.img} />
            <Version position="absolute" />
          </ImageBottomBar>

          <DetailBottomBar withShadow="false">
            <DetailBottomBarConfirm
              name={
                loading
                  ? intl.formatMessage({ id: 'general.loading' })
                  : intl.formatMessage({ id: 'specific-code.verify-code' })
              }
              onClick={e => {
                this.validateForm(() => {
                  checkCode().then(() => {
                    this.continue(e);
                  });
                });
              }}
            />
          </DetailBottomBar>
        </FocusBottomBar>
      </View>
    );

    return (
      <Page title={intl.formatMessage({ id: 'specific-code.page-title' })}>
        <View isExpanded style={styles.page}>
          <View style={{ width: 500 }}>
            <FocusContent>
              <FocusBody>
                <View
                  isExpanded
                  isVertical
                  style={{ padding: 40, paddingBottom: 10, alignItems: 'center', width: '100%' }}>
                  <FormRow>
                    <Heading style={styles.header} scale={1.5}>
                      {intl.formatMessage({ id: 'specific-code.welcome' })}
                    </Heading>
                  </FormRow>
                  <FormRow>
                    <Text>{intl.formatMessage({ id: 'specific-code.welcome-subtext' })}</Text>
                  </FormRow>
                  <FormRow>
                    <Text style={styles.regCodeText}>
                      {intl.formatMessage({ id: 'specific-code.reg-code-text' })}
                    </Text>
                  </FormRow>
                  <FormRow>
                    <TextInput
                      hasError={codeInputError}
                      label={intl.formatMessage({ id: 'specific-code.code-label' })}
                      value={values.code}
                      styleInput={{ width: '420px' }}
                      styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                      onChange={this.handleChange('code')}
                    />
                    {ValidationMessageRef}
                  </FormRow>
                </View>

                <Mutation
                  mutation={specCodeMutation}
                  variables={{ code: values.code }}
                  onCompleted={() => {}}>
                  {(checkCode, { error, loading }) => (
                    <DynamicBottomBar checkCode={checkCode} loading={loading} error={error} />
                  )}
                </Mutation>
              </FocusBody>
            </FocusContent>
          </View>
        </View>
      </Page>
    );
  }
}

export default injectIntl(SpecificCodeForm);
